import dayjs from 'dayjs';
import * as Yup from 'yup';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, UseFormReturn } from 'react-hook-form';
import React, {
  useState,
  useEffect,
  forwardRef,
  ChangeEvent,
  ForwardedRef
} from 'react';

import Box from '@mui/material/Box';
import {
  Card,
  MenuItem,
  IconButton,
  Typography,
  InputAdornment,
  Button,
  Grid
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { Endpoints, useApiRepo } from '../utils/Api';

import {
  Homework,
  ERPHomework,
  HomeworkType,
  HomeworkTypes,
  HomeworkTrial,
  isERPHomework,
  TaskHomework,
  isTaskHomework
} from '../utils/Homework';

import PatientHomeworkDueDateField from './HomeworkDueDateField';
import { FormProvider, RHFSelect, RHFTextField } from './hook-form';
import { IObsession } from '../views';
import { LinkButton } from './LinkButton';

// ----------------------------------------------------------------------

dayjs.extend(utc);
dayjs.extend(timezone);

type HomeworkFormProps = {
  onClose: (newHomework?: Homework) => void;
  homework: Homework;
  operation: 'update' | 'create';
  formRef?: ForwardedRef<UseFormReturn<any>>; // Reference to the form
};

const HomeworkForm = forwardRef<UseFormReturn<any>, HomeworkFormProps>(
  ({ homework, onClose, operation, formRef: _ }, ref) => {
    const { enqueueSnackbar } = useSnackbar();
    const { getObjectsFromAPI } = useApiRepo();
    const [obsessions, setObsessions] = React.useState<IObsession[]>([]);

    const HomeworkSchema = Yup.object().shape({
      id: Yup.string(),
      homeworkType: Yup.string<HomeworkType>()
        .required('Homework Type is required')
        .nullable()
        .test(
          'homeworkType-required',
          'Homework Type is required',
          (value) => value !== null && value !== undefined // Check for null or undefined
        ),
      description: Yup.string().required('Description is required'),
      purpose: Yup.string().optional().nullable(),
      obsessionId: Yup.string()
        .nullable()
        .when('homeworkType', {
          is: (val: HomeworkType) => val === 'erp',
          then: (s) => s.required('Obsession is required'),
          otherwise: (s) => s.optional()
        }),
      compulsionId: Yup.string()
        .nullable()
        .when('homeworkType', {
          is: (val: HomeworkType) => val === 'erp',
          then: (s) => s.required('Compulsion is required'),
          otherwise: (s) => s.optional()
        }),
      triggerId: Yup.string()
        .nullable()
        .when('homeworkType', {
          is: (val: HomeworkType) => val === 'erp',
          then: (s) => s.required('Trigger is required'),
          otherwise: (s) => s.optional()
        }),
      dueDate: Yup.date()
        .required('Due Date is required')
        .test(
          'dueDate-future',
          'Due date must be in the future',
          (value) => value >= dayjs(new Date()).startOf('day').toDate()
        ),
      trials: Yup.array(
        Yup.object().shape({
          id: Yup.string(),
          order: Yup.number(),
          goalSeconds: Yup.number()
            .required('Goal seconds are required')
            .moreThan(0, 'Goal seconds must be greater than 0'),
          submittedSeconds: Yup.number().optional().nullable()
        })
      )
        .optional()
        .nullable(),
      task: Yup.object().shape({}).optional().nullable(),
      canceled: Yup.bool().required()
    });

    // get obsessions
    async function getObsessions() {
      const api = await getObjectsFromAPI();
      const res = await api.get(Endpoints.obessions);
      setObsessions(res.data.data);
    }

    React.useEffect(() => {
      getObsessions();
    }, []);

    const methods = useForm({
      resolver: yupResolver(HomeworkSchema)
    });

    // Assign the form methods to the ref
    useEffect(() => {
      if (ref) {
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        ref.current = methods;
      }
    }, [ref, methods]);

    const {
      reset,
      handleSubmit,
      watch,
      setValue
      // formState, // useful to log errors
    } = methods;

    useEffect(() => {
      const defaultValues = {
        id: homework.id,
        homeworkType: homework.homeworkType ?? null,
        description: homework.description,
        purpose: homework.purpose,
        obsessionId: homework.obsessionId ?? null,
        compulsionId: homework.compulsionId ?? null,
        triggerId: homework.triggerId ?? null,
        canceled: homework.canceled,
        dueDate: homework.dueDate
          ? dayjs
              .tz(homework.dueDate, 'utc')
              .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
              .toDate()
          : new Date(),
        trials: (homework as ERPHomework).trials,
        task: (homework as TaskHomework).task
      };
      reset(defaultValues);
    }, []);

    const currentObsessionId = watch('obsessionId');
    const currentCompulsionId = watch('compulsionId');
    const currentTriggerId = watch('triggerId');
    const trials = watch('trials');
    const homeworkType = watch('homeworkType');

    const [trialsError, setTrialsError] = useState<string | null>(null);
    useEffect(() => {
      // Check for error condition
      if (homeworkType !== 'erp') {
        setTrialsError(null);
        return;
      }

      if (!trials) {
        setTrialsError(`At least 1 trial is required.`);
      } else if (trials.length < 1) {
        setTrialsError(`At least 1 trial is required.`);
      } else if (trials.length > 10) {
        setTrialsError(`Trials cannot exceed 10.`);
      } else {
        setTrialsError(null);
      }
    }, [trials, homeworkType]);

    const handleHomeworkTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
      const newHomeworkType = event.target.value as HomeworkType;

      if (newHomeworkType === homeworkType) {
        // no change
        return;
      }

      if (newHomeworkType === 'erp') {
        setValue('task', undefined);
        setValue('trials', [
          { id: crypto.randomUUID(), order: 1, goalSeconds: undefined }
        ] as any); // working around schema having goalSeconds required, but initial value being unset
      } else if (newHomeworkType === 'task') {
        setValue('task', {});
        setValue('trials', undefined);
      }

      setValue('homeworkType', newHomeworkType);
    };

    const handleChangeTrialCount = (event: ChangeEvent<HTMLInputElement>) => {
      const newTrials = trials ?? [];

      const val = parseInt(event.currentTarget.value, 10);

      if (val > 10) {
        setTrialsError(`Trials cannot exceed 10.`);
        return;
      }

      if (val > newTrials.length) {
        for (let index = newTrials.length; index < val; index += 1) {
          (newTrials as any).push({
            id: crypto.randomUUID(),
            order: newTrials.length + 1
          }); // working around schema having goalSeconds required, but initial value being unset
        }
      } else {
        for (let index = newTrials.length; index > val; index -= 1) {
          if (newTrials[index - 1].submittedSeconds) {
            // can't remove this trial as it already has a submission
            break;
          }

          newTrials.pop();
        }
      }

      setValue('trials', newTrials, {
        shouldDirty: true,
        shouldValidate: true
      });
    };

    const handleIncrementTrialCount = () => {
      const newTrials = trials ?? [];
      (newTrials as any).push({
        id: crypto.randomUUID(),
        order: newTrials.length + 1
      }); // working around schema having goalSeconds required, but initial value being unset

      setValue('trials', newTrials, {
        shouldDirty: true,
        shouldValidate: true
      });
    };

    const handleDecrementTrialCount = () => {
      const newTrials = trials ?? [];

      if (newTrials.length === 0) {
        // nothing to remove
        return;
      }

      if (newTrials[newTrials.length - 1].submittedSeconds) {
        // can't remove this trial as it already has a submission
        return;
      }

      newTrials.pop();

      setValue('trials', newTrials, {
        shouldDirty: true,
        shouldValidate: true
      });
    };

    const hasTrialSubmissions = () =>
      isERPHomework(homework) &&
      homework.trials?.some((t) => t.submittedSeconds);
    const hasTaskSubmission = () =>
      isTaskHomework(homework) && !!homework.completedTs;
    const hasSubmissions = () => hasTrialSubmissions() || hasTaskSubmission();
    const hasTrialSubmission = (index: number) =>
      isERPHomework(homework) && !!homework.trials?.[index]?.submittedSeconds;

    const onUpdate = handleSubmit(async (_data) => {
      // TODO: update

      onClose();
    });

    const onCreate = handleSubmit(async (data) => {
      // create
      const api = await getObjectsFromAPI();

      try {
        const res = await api.post(Endpoints.homework, {
          ...data,
          dueDate: new Date(data.dueDate!).toISOString().split('T')[0] // Format as YYYY-MM-DD
        });
        onClose(res.data.data);
      } catch (error) {
        enqueueSnackbar('Failed to create homework.  Please try again.', {
          variant: 'error'
        });

        // eslint-disable-next-line no-console
        console.error(error); // TODO: log to sentry
      }
    });

    const close = () => {
      reset();
      onClose();
    };

    return (
      <FormProvider
        methods={methods}
        onSubmit={operation === 'create' ? onCreate : onUpdate}
      >
        <Box sx={{ display: 'flex', height: '100%' }}>
          <Card
            sx={{
              p: 2,
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)'
              }}
              sx={{
                marginTop: '10px'
              }}
            >
              {homeworkType !== undefined && (
                <RHFSelect
                  name="homeworkType"
                  label="Homework Type"
                  onChange={handleHomeworkTypeChange}
                  disabled={hasSubmissions()}
                >
                  {Object.entries(HomeworkTypes).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value}
                    </MenuItem>
                  ))}
                </RHFSelect>
              )}

              <RHFTextField name="description" label="Description" />
              <RHFTextField name="purpose" label="Purpose" />
              <PatientHomeworkDueDateField />

              {currentObsessionId !== undefined && (
                <RHFSelect name="obsessionId" label="Obsession">
                  {obsessions
                    .filter((o) => o.active)
                    .map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.description}
                      </MenuItem>
                    ))}
                </RHFSelect>
              )}

              {currentCompulsionId !== undefined && (
                <RHFSelect name="compulsionId" label="Compulsion">
                  {(
                    obsessions.find((o) => o.id === currentObsessionId) ?? {
                      compulsions: []
                    }
                  ).compulsions
                    .filter((c) => c.active)
                    .map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.compulsion}
                      </MenuItem>
                    ))}
                </RHFSelect>
              )}

              {currentTriggerId !== undefined && (
                <RHFSelect name="triggerId" label="Trigger">
                  {(
                    obsessions.find((o) => o.id === currentObsessionId) ?? {
                      triggers: []
                    }
                  ).triggers
                    .filter((t) => t.active)
                    .map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.trigger}
                      </MenuItem>
                    ))}
                </RHFSelect>
              )}

              {homeworkType === 'erp' && (
                <RHFTextField
                  name="trials"
                  label="Trials"
                  type="tel"
                  value={trials?.length}
                  onChange={handleChangeTrialCount}
                  error={!!trialsError} // Display error if trialsError is not null
                  helperText={trialsError} // Display the error message
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleIncrementTrialCount}
                          disabled={trials?.length! >= 10}
                          size="small"
                        >
                          <Icon icon="eva:arrow-ios-upward-fill" />
                        </IconButton>
                        <IconButton
                          onClick={handleDecrementTrialCount}
                          disabled={trials?.length === 1}
                          size="small"
                        >
                          <Icon icon="eva:arrow-ios-downward-fill" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              )}
            </Box>

            {homeworkType === 'erp' && (
              <>
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ mt: 2, mb: 2, gap: 2 }}
                >
                  <Typography variant="h6">Trials</Typography>
                </Box>
                <Grid container spacing={2}>
                  {((trials as HomeworkTrial[]) || []).map((trial, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={trial.id}>
                      <RHFTextField
                        name={`trials.${index}.goalSeconds`}
                        label={`Trial ${trial.order} Goal Seconds`}
                        type="tel"
                        variant="outlined"
                        fullWidth
                        disabled={hasTrialSubmission(index)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 2,
                mb: 2
              }}
            >
              <LinkButton buttonText="Cancel" handleClick={() => close()} />

              {operation === 'update' && (
                <Button
                  type="submit"
                  color="inherit"
                  style={{
                    padding: '10px 30px 10px 30px',
                    color: '#010d0d',
                    border: 'solid 2px #010d0d',
                    borderRadius: 30,
                    fontSize: '20px',
                    backgroundColor: 'transparent'
                  }}
                >
                  <Typography variant="h6">Update</Typography>
                </Button>
              )}

              {operation === 'create' && (
                <Button
                  type="submit"
                  color="inherit"
                  style={{
                    padding: '10px 30px 10px 30px',
                    color: '#010d0d',
                    border: 'solid 2px #010d0d',
                    borderRadius: 30,
                    fontSize: '20px',
                    backgroundColor: 'transparent'
                  }}
                >
                  <Typography variant="h6">Create</Typography>
                </Button>
              )}
            </Box>
          </Card>
        </Box>
      </FormProvider>
    );
  }
);

export default HomeworkForm;
